// @spec TSK-STR-002
// @spec TSK-STR-004
// @spec TSK-STR-005
// @spec TSK-STR-006
// @spec TSK-STR-035
// @spec TSK-STR-039
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, AlertCircle, Ban, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  CompleteTaskButton,
  GHGLinkDisplay,
  TaskApprovalActions,
  TaskDetailActions,
  TaskDetailDescription,
  TaskDetailEvidence,
  TaskDetailHeader,
  TaskDetailHistory,
  TaskDetailMetadata,
  TaskDetailSkeleton,
  TaskDetailWorkflow,
  useGHGLink,
  useTask,
} from '@/features/tasks';
import { AssignToMeCard } from '@/features/tasks/components/AssignToMeCard';
import { TaskContributions } from '@/features/tasks/components/TaskContributions';
import { TaskDeepLinkButton } from '@/features/tasks/components/TaskDeepLinkButton';
import { TaskSubmittedEvidence } from '@/features/tasks/components/TaskSubmittedEvidence';
import { useActiveOrganizationId } from '@/features/tasks/hooks/use-active-organization-id';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';

export const Route = createFileRoute('/_authenticated/tasks/$taskId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.taskDetails'),
  }),
  component: TaskDetailPage,
});

/**
 * Task detail page - Full task view.
 *
 * Displays complete task information including:
 * - Title, status, and priority
 * - Due date and timing information
 * - Assignee and assigner details
 * - Description and instructions
 * - Evidence requirements
 * - Workflow context (if applicable)
 */
function TaskDetailPage() {
  const { t } = useTranslation('tasks');
  const { taskId } = Route.useParams();
  const navigate = useNavigate();

  const { task, isLoading, isError, error, refetch } = useTask({ taskId });

  // Get organization ID using the same hook as the rest of the tasks domain
  const { organizationId, isLoading: isOrgLoading } = useActiveOrganizationId();
  const activeOrgId = organizationId;

  // Fetch GHG link if task has one
  const hasGhgLink = task?.sourceReferenceType === 'ghg_link';
  const { ghgLink } = useGHGLink({
    taskId,
    enabled: hasGhgLink,
  });

  // Handle back navigation
  const handleBack = () => {
    void navigate({ to: '/tasks/my' });
  };

  // Determine error type for appropriate UI
  const isNotFound = error instanceof ApiClientError && (error.status === 404 || error.status === 422);
  const isForbidden = error instanceof ApiClientError && error.status === 403;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={handleBack} className="-ml-2">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('page.backToMyTasks')}
        </Button>

        {/* Loading state (task loading or org context resolving) */}
        {(isLoading || isOrgLoading) && <TaskDetailSkeleton />}

        {/* Not found error */}
        {isNotFound && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detail.taskNotFound')}</AlertTitle>
            <AlertDescription>
              {t('detail.taskNotFoundDescription')}
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleBack}>
              {t('detail.returnToMyTasks')}
            </Button>
          </Alert>
        )}

        {/* Forbidden error */}
        {isForbidden && (
          <Alert variant="destructive">
            <Ban className="h-4 w-4" />
            <AlertTitle>{t('detail.accessDenied')}</AlertTitle>
            <AlertDescription>{t('detail.accessDeniedDescription')}</AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleBack}>
              {t('detail.returnToMyTasks')}
            </Button>
          </Alert>
        )}

        {/* Generic error */}
        {isError && !isNotFound && !isForbidden && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detail.errorLoadingTask')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : t('detail.failedToLoadDetails')}
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()}>
              {t('detail.tryAgain')}
            </Button>
          </Alert>
        )}

        {/* No organization context */}
        {!isLoading && !isOrgLoading && !task && !isError && !activeOrgId && (
          <div className="rounded-lg border border-muted p-6 text-center">
            <Building2 className="mx-auto h-8 w-8 text-muted-foreground mb-3" aria-hidden="true" />
            <p className="text-sm text-muted-foreground mb-4">
              {t('detail.noOrgSelectedDescription')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void navigate({ to: '/settings/organizations' })}
            >
              {t('detail.goToOrganizations')}
            </Button>
          </div>
        )}

        {/* Task content */}
        {task && !isLoading && (
          <div className="space-y-6">
            <TaskDetailHeader task={task} />

            {/* Deep link button to domain data entry */}
            <TaskDeepLinkButton task={task} ghgLink={ghgLink ?? null} />

            {/* Action buttons for task status transitions */}
            {task.status === 'in_progress' && (
              <div className="flex gap-3">
                <CompleteTaskButton task={task} onSuccess={() => void refetch()} />
              </div>
            )}

            {/* Approval actions for tasks pending review */}
            <TaskApprovalActions
              task={task}
              onApproved={() => void refetch()}
              onRejected={() => void refetch()}
            />

            {/* Submitted evidence visible to approver */}
            {task.status === 'pending_review' && (
              <TaskSubmittedEvidence
                taskId={task.id}
                customFields={task.customFields ?? {}}
              />
            )}

            {task.assigneeId === null &&
              organizationId &&
              !['completed', 'cancelled'].includes(task.status) && (
                <AssignToMeCard
                  taskId={task.id}
                  orgId={organizationId}
                  onSuccess={() => void refetch()}
                />
              )}
            <TaskDetailActions task={task} onTaskUpdate={() => void refetch()} />
            <TaskDetailMetadata task={task} />
            {ghgLink && <GHGLinkDisplay ghgLink={ghgLink} />}
            <TaskContributions taskId={taskId} />
            <TaskDetailDescription
              description={task.description}
              instructions={task.instructions}
            />
            <TaskDetailEvidence
              evidenceConfig={task.evidenceConfig}
              requiresApproval={task.requiresApproval}
            />
            <TaskDetailWorkflow
              workflowInstanceId={task.workflowInstanceId}
              workflowStepId={task.workflowStepId}
            />
            {organizationId && (
              <TaskDetailHistory taskId={taskId} organizationId={organizationId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
